import { useEffect, useMemo, useRef, useState } from 'react';
import { StanButton, StanDraggable, StanModal } from '@/components';
import { DraggableWrapper } from '@/components/core-components/StanDraggable/DraggableWrapper';
import type { DragEvent } from '@/components/core-components/StanDraggable/StanDraggable.types';
import { cn } from '@/lib/utils/cn';
import { PageStatus } from '@/types/store';
import { buildFunnelPageTree, buildFunnelPageTreePositions, getFunnelPageRelationships } from '../../../logic';
import type { FunnelPageItem, FunnelPageProductInfo } from '../../../types';
import { DeleteFunnelPageModal } from './DeleteFunnelPageModal';
import { ManageBranchSubtree } from './manage-pages/ManageBranchSubtree';
import { ManageRow } from './manage-pages/ManageRow';
import { buildGroupsFromPages, type PageGroup, type PageSlot } from './manage-pages/manageFunnelPages.logic';

interface ManageFunnelPagesModalProps {
    isOpen: boolean;
    onClose: () => void;
    pages: FunnelPageItem[];
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    isDeleting: boolean;
    onConfirmReorder: (orderedSavedPageIds: number[]) => void;
    onHidePage: (pageId: number) => void;
    onDuplicatePage: (pageId: number) => void;
    onDeletePage: (pageId: number) => void;
}

export const ManageFunnelPagesModal = ({
    isOpen,
    onClose,
    pages,
    productInfoByPageId,
    isDeleting,
    onConfirmReorder,
    onHidePage,
    onDuplicatePage,
    onDeletePage,
}: ManageFunnelPagesModalProps) => {
    const initialGroups = useMemo(() => buildGroupsFromPages(pages), [pages]);
    const [orderedGroups, setOrderedGroups] = useState<PageGroup[]>(initialGroups);
    const [pageToDelete, setPageToDelete] = useState<FunnelPageItem | null>(null);
    const [draggingRootId, setDraggingRootId] = useState<number | null>(null);
    const [newlyAddedPageId, setNewlyAddedPageId] = useState<number | null>(null);
    const prevPageIdsRef = useRef<Set<number> | null>(null);

    const relationshipByPageId = useMemo(() => getFunnelPageRelationships(pages), [pages]);
    const positionByPageId = useMemo(() => buildFunnelPageTreePositions(buildFunnelPageTree(pages)), [pages]);

    useEffect(() => {
        if (isOpen) {
            setOrderedGroups(buildGroupsFromPages(pages));
        }
    }, [isOpen, pages]);

    // Briefly highlight a newly-appearing page (e.g. after duplicate) with a
    // bounce-in animation so the user can see where it landed inside the modal.
    useEffect(() => {
        const currentIds = new Set(pages.map(p => p.page_id));
        const prev = prevPageIdsRef.current;
        prevPageIdsRef.current = currentIds;

        if (!prev) return undefined;
        const added = [...currentIds].filter(id => !prev.has(id));
        if (added.length !== 1) return undefined;

        const id = added[0] || 0;
        setNewlyAddedPageId(id);
        const timer = setTimeout(() => {
            setNewlyAddedPageId(current => (current === id ? null : current));
        }, 600);
        return () => clearTimeout(timer);
    }, [pages]);

    useEffect(() => {
        setOrderedGroups(prev => {
            const latest = buildGroupsFromPages(pages);
            const byRootId = new Map(latest.map(g => [g.rootId, g]));
            const kept = prev.map(g => byRootId.get(g.rootId)).filter((g): g is PageGroup => !!g);
            const keptIds = new Set(kept.map(g => g.rootId));
            const added = latest.filter(g => !keptIds.has(g.rootId));
            return [...kept, ...added];
        });
    }, [pages]);

    const handleReorder = (event: DragEvent) => {
        const byRootId = new Map(orderedGroups.map(g => [String(g.rootId), g]));
        const next = event.allItems.map(item => byRootId.get(String(item.id))).filter((g): g is PageGroup => !!g);
        setOrderedGroups(next);
    };

    const handleDragStart = (event: DragEvent) => {
        setDraggingRootId(Number(event.item.id));
    };

    const handleDragEnd = () => {
        setDraggingRootId(null);
    };

    const flattenedOrderedIds = orderedGroups.flatMap(g => g.pages.map(p => p.page_id));
    const originalOrderedIds = initialGroups.flatMap(g => g.pages.map(p => p.page_id));
    const hasOrderChanges = flattenedOrderedIds.some((id, i) => id !== originalOrderedIds[i]);

    const handleConfirmReorder = () => {
        onConfirmReorder(flattenedOrderedIds);
        onClose();
    };

    const handleConfirmDelete = () => {
        if (!pageToDelete) return;
        onDeletePage(pageToDelete.page_id);
        setPageToDelete(null);
    };

    let runningStep = 0;

    return (
        <>
            <StanModal id='manage-funnel-pages-modal' size='md' isOpen={isOpen} onClose={onClose} title='Manage Pages'>
                <div className='flex flex-col gap-6'>
                    <p className='text-body-md text-gray -mt-2 text-center'>
                        Drag to reorder, or use the menu to hide, duplicate, or delete a page.
                    </p>

                    <div className='flex flex-col gap-2'>
                        <StanDraggable
                            onReorder={handleReorder}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            direction='vertical'
                            gap='sm'
                        >
                            {orderedGroups.map(group => {
                                const rootStep = positionByPageId.get(group.slot.page.page_id) ?? ++runningStep;
                                const branchChildren = (group.slot.children ?? []).filter(
                                    (c): c is PageSlot => c.kind === 'page'
                                );
                                const isDraggingThis = draggingRootId === group.rootId;
                                const isRootHidden = group.slot.page.status === PageStatus.DRAFT;

                                return (
                                    <DraggableWrapper
                                        key={group.rootId}
                                        id={String(group.rootId)}
                                        className={cn('w-full', isDraggingThis && 'relative z-50')}
                                    >
                                        <div
                                            className={cn(
                                                'flex w-full flex-col gap-1.5 rounded-lg border border-transparent transition-[background-color,box-shadow,opacity,border-color] duration-200 ease-out',
                                                isDraggingThis &&
                                                    'bg-brand-subtle border-brand-selected shadow-stan opacity-95'
                                            )}
                                        >
                                            <ManageRow
                                                page={group.slot.page}
                                                stepNumber={rootStep}
                                                productInfo={productInfoByPageId[group.slot.page.page_id]}
                                                relationship={relationshipByPageId.get(group.slot.page.page_id)}
                                                isNew={group.slot.page.page_id === newlyAddedPageId}
                                                onHide={() => onHidePage(group.slot.page.page_id)}
                                                onDuplicate={() => onDuplicatePage(group.slot.page.page_id)}
                                                onDelete={() => setPageToDelete(group.slot.page)}
                                            />

                                            {branchChildren.length > 0 && (
                                                <div className='border-border-subtle ml-9 flex flex-col gap-1.5 border-l pb-1.5 pl-3'>
                                                    {branchChildren.map(branch => (
                                                        <ManageBranchSubtree
                                                            key={branch.page.page_id}
                                                            slot={branch}
                                                            stepNumber={positionByPageId.get(branch.page.page_id) ?? 0}
                                                            parentStepNumber={rootStep}
                                                            productInfoByPageId={productInfoByPageId}
                                                            relationshipByPageId={relationshipByPageId}
                                                            positionByPageId={positionByPageId}
                                                            onHide={onHidePage}
                                                            onDuplicate={onDuplicatePage}
                                                            onDelete={page => setPageToDelete(page)}
                                                            isAncestorHidden={isRootHidden}
                                                            newlyAddedPageId={newlyAddedPageId}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </DraggableWrapper>
                                );
                            })}
                        </StanDraggable>
                    </div>

                    <div className='flex w-full gap-1 pt-2 max-sm:flex-col-reverse sm:gap-2'>
                        <StanButton
                            name='cancel-manage-funnel-pages'
                            variant='outline'
                            onClick={onClose}
                            className='w-full'
                        >
                            Cancel
                        </StanButton>
                        <StanButton
                            name='confirm-manage-funnel-pages'
                            onClick={handleConfirmReorder}
                            disabled={!hasOrderChanges}
                            className='w-full'
                        >
                            Save Order
                        </StanButton>
                    </div>
                </div>
            </StanModal>

            <DeleteFunnelPageModal
                isOpen={!!pageToDelete}
                onClose={() => setPageToDelete(null)}
                pageTitle={pageToDelete?.title ?? 'this page'}
                isDeleting={isDeleting}
                onConfirm={handleConfirmDelete}
            />
        </>
    );
};
